<template>
  <div class="location-info">
    <div class="location-info__contacts">
      <div
        class="building-contacts__property u-sec-margin-bottom"
        v-for="(department, key) in contactsMap"
      >
        <label class="building-contacts__contacts--label">{{ $t(`tenancy.${key}`) }}</label>
        <div class="location-info__contacts--group">
          <div class="location-info__contacts--title">
            <div class="title__name">{{ $t('settings.building.name') }}</div>
            <div class="title__role">{{ $t('settings.building.role') }}</div>
          </div>
          <ul class="location-info__contacts--list">
            <li
              class="list__item"
              v-for="(item, index) in department"
              :key="index"
              :class="!item.userId ? 'error-highlight' : ''"
            >
              <HKLSelect
                v-if="isEditable"
                class="list__item--name"
                :disabled="false"
                :required="true"
                :options="contactHklOptions"
                :value="item.userId"
                :showSearch="true"
                placeholder=""
                @change="val => updateOneContactById(item.hashIndex, val)"
              />
              <div
                class="list__item--name"
                v-else
              >{{ item.displayName | isEmptyValue }}</div>
              <div class="list__item--role-group">
                <InputField
                  :showFlag="false"
                  :showColon="false"
                  class="list__item--role"
                  extendClass="location-info__telephone--input"
                  :readonly="!isEditable"
                  v-model="item.title"
                />
                <div
                  class="list__item--delete"
                  v-if="isEditable"
                  @click="deleteOneContact(item.hashIndex)"
                ><i class="fa fa-times fa-lg" /></div>
              </div>
            </li>
          </ul>
        </div>
        <div
          class="location-info__contacts--add"
          v-if="isEditable"
          @click="addOneContact(key)"
        >{{ $t('settings.building.addNewHkldContact') }} + </div>
      </div>
    </div>
  </div>
</template>

<script>
import md5 from 'md5'
import { cloneDeep } from 'lodash'
import { getLocationContacts } from '@/api/locations/request'
import TextField from '@/controls/TextField'
import InputField from '@/controls/InputField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { locationContactsDepartment } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'

export default {
  name: 'SiteContacts',
  components: {
    TextField,
    InputField,
    HKLSelect,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: () => false,
    },
    users: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      locationContacts: [],
      rawContacts: [],
      locationContactsDepartment,
    }
  },
  methods: {
    findContactIndex (hashIndex) {
      return this.locationContacts.findIndex(item => item.hashIndex === hashIndex)
    },
    updateOneContact (hashIndex, val) {
      const index = this.findContactIndex(hashIndex)
      this.locationContacts[index].userId = val.id
      this.locationContacts[index].displayName = val.displayName
    },
    updateOneContactById (hashIndex, id) {
      const contact = this.contactOptions.find(item => item.id === id)
      if (contact) {
        this.updateOneContact(hashIndex, contact)
      }
    },
    addOneContact (department) {
      const hashIndex = md5(`${new Date()}${department}`)
      const result = {
        department: this.locationContactsDepartment[department],
        title: '',
        locationId: this.siteId,
        userId: '',
        hashIndex,
      }
      const len = this.locationContacts.length
      this.locationContacts.splice(len, 1, result)
    },
    deleteOneContact (hashIndex) {
      const index = this.findContactIndex(hashIndex)
      this.locationContacts.splice(index, 1)
    },
    filterByDepartment (department) {
      return this.parsedContacts.filter(item => item.department === department)
    },
    validate () {
      const formElement = '.location-info__contacts'
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, formElement)
      return fusionInputCheckUtil.isAllFieldsValid(formElement)
    },
    reset () {
      this.locationContacts = cloneDeep(this.rawContacts)
    },
    async getContacts () {
      const result = await getLocationContacts(this.siteId)
      this.rawContacts = cloneDeep(result)
      this.locationContacts = cloneDeep(result)
    },
    getUpdatedContacts () {
      const result = this.locationContacts.map(item => {
        let rs = {
          title: item.title,
          department: item.department,
          locationId: item.locationId,
          userId: item.userId,
        }
        if (item.id) {
          rs.id = item.id
        }
        return rs
      })
      return result
    },
  },
  computed: {
    siteId () {
      return this.$route.params.id
    },
    contactOptions () {
      return this.users
    },
    contactHklOptions () {
      return this.contactOptions.map((contact) => ({
        name: contact.displayName,
        value: contact.id,
      }))
    },
    contactsMap () {
      const keys = Object.keys(this.locationContactsDepartment)
      const result = {
        [keys[0]]: this.propertyManagementContacts,
        [keys[1]]: this.assetManagementContacts,
        [keys[2]]: this.technicalServicesContacts,
      }
      return result
    },
    parsedContacts () {
      return this.locationContacts.map(item => {
        item.displayName = item.user ? item.user.displayName : null
        item.hashIndex = md5(`${item.userId}${item.title}`)
        return item
      })
    },
    propertyManagementContacts () {
      const result = this.filterByDepartment(locationContactsDepartment.propertyManagement)
      return result
    },
    assetManagementContacts () {
      const result = this.filterByDepartment(locationContactsDepartment.assetManagement)
      return result
    },
    technicalServicesContacts () {
      const result = this.filterByDepartment(locationContactsDepartment.technicalServices)
      return result
    },
  },
  async mounted () {
    await this.getContacts()
  },
}
</script>

<style lang="scss" scoped>
.location-info {
  .u-sec-margin-bottom {
    margin-bottom: 25px;
  }
  &__location {
    width: 50%;
  }
  &__telephone {
    width: 25%;
  }
  &__contacts {
    margin-top: 15px;
    &--label {
      width: 188px;
      height: 20px;
      font-family: Verdana;
      font-size: 14px;
      font-weight: bold;
      line-height: 1.43;
      color: #0c3a58;
    }
    &--group {
      border: solid 1px #ebebeb;
    }
  }
  &__contacts--title {
    max-width: 808px;
    margin-top: 10px;
    padding: 0 20px;
    height: 40px;
    background-color: #ffffff;
    border-bottom: solid 1px #ebebeb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .title__name, .title__role {
      height: 20px;
      font-size: 14px;
      font-weight: bold;
      color: #083e62;
    }
    .title__name {
      width: 40%;
    }
    .title__role {
      width: 55%;
    }
  }
  &__contacts--list {
    list-style: none;
    margin-left: 0;
    max-width: 808px;
    .list__item {
      height: 40px;
      padding: 0px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      &--role-group {
        width: 55%;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      &--name {
        width: 40%;
      }

      &--role {
        width: 95%;
        input {
          margin-bottom: 0;
        }
      }
      &--delete {
        font-size: 18px;
        color: #9B9B9B;
        margin-left: 80px;
        cursor: pointer;
        cursor: hand;
      }
    }
    li:nth-child(odd) {
      background-color: #f8f8f8;
    }
    li:nth-child(even) {
      background-color: #ffffff;
    }
  }
  &__contacts--add {
    width: 270px;
    height: 18px;
    font-size: 14px;
    font-style: italic;
    text-align: left;
    color: #0c3a58;
    text-decoration: underline;
    cursor: pointer;
  }
  .building-contacts {
    &__property, &__asset, &__technical {
      .building-contacts__contacts--label {
        color: #0c3a58;
        font-weight: bold;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
<style lang="scss">
.error-highlight {
  .hkl-select.list__item--name,
  .multiselect.list__item--name {
    .ant-select-selection,
    .multiselect__select,
    input {
      background-color: #f9ecea;
    }
  }
}
.location-info__telephone--input {
  outline: none;
}
.location-info__contacts--list .list__item .list__item--role input {
  margin-bottom: 0;
}
</style>
